/**
 * Profile - 个人中心页面
 * 侧边栏导航 + 内容区域布局
 */
import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth, PrivateRoute } from '../../contexts/AuthContext';
import { 
  BookOpen, 
  Wallet, 
  CreditCard, 
  ShoppingBag, 
  Bell, 
  Settings,
  ChevronRight,
  ArrowLeft,
  Crown,
} from 'lucide-react';

// 子页面组件
import MyStories from './tabs/MyStories';
import MyWallet from './tabs/MyWallet';
import Subscription from './tabs/Subscription';
import RechargeHistory from './tabs/RechargeHistory';
import ConsumptionHistory from './tabs/ConsumptionHistory';
import Notifications from './tabs/Notifications';
import AccountSettings from './tabs/AccountSettings';

const menuItems = [
  { id: 'stories', label: '我的绘本', icon: BookOpen, component: MyStories },
  { id: 'wallet', label: '我的钱包', icon: Wallet, component: MyWallet },
  { id: 'subscription', label: '充值优惠', icon: Crown, component: Subscription },
  { id: 'recharge', label: '充值记录', icon: CreditCard, component: RechargeHistory },
  { id: 'consumption', label: '消费记录', icon: ShoppingBag, component: ConsumptionHistory },
  { id: 'notifications', label: '消息通知', icon: Bell, component: Notifications },
  { id: 'settings', label: '账号设置', icon: Settings, component: AccountSettings },
];

function ProfileContent() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'stories');

  // 当 URL 参数变化时更新当前 tab
  useEffect(() => {
    const tab = searchParams.get('tab') || 'stories';
    setActiveTab(tab);
  }, [searchParams]);

  // 切换 tab 时更新 URL
  const handleTabChange = (tabId: string) => {
    setSearchParams({ tab: tabId });
  };

  const activeMenuItem = menuItems.find(item => item.id === activeTab);
  const ActiveComponent = activeMenuItem?.component || MyStories;

  return (
    <div className="min-h-screen bg-slate-50">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-medium">返回首页</span>
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* 侧边栏 */}
          <div className="lg:col-span-3">
            <div className="sticky top-24 flex flex-col gap-6">
              {/* 用户信息卡片 */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                <div className="flex gap-4 items-center">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center text-white text-xl font-bold">
                    {user?.avatar ? (
                      <img src={user.avatar} alt="avatar" className="w-full h-full rounded-full object-cover" />
                    ) : (
                      user?.nickname?.[0]?.toUpperCase() || 'U'
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="font-bold text-slate-800 truncate">{user?.nickname}</h2>
                    <p className="text-sm text-slate-500 truncate">{user?.email || user?.phone}</p>
                  </div>
                </div>

                {/* 余额显示 */}
                <div className="bg-slate-50 rounded-xl p-4 mt-4">
                  <div className="flex items-center text-sm text-slate-600 gap-2 mb-2">
                    <Wallet className="w-4 h-4" />
                    <span>账户余额</span>
                  </div>
                  <div className="text-2xl font-bold text-indigo-600">
                    ¥{user?.balance?.toFixed(2) || '0.00'}
                  </div>
                </div>

                {/* 剩余创作额度已移除：当前使用体验金模式，页数自定义 */}
              </div>

              {/* 菜单导航 */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-2">
                {menuItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeTab === item.id;
                  return (
                    <button
                      key={item.id}
                      onClick={() => handleTabChange(item.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left font-medium transition-colors ${
                        isActive
                          ? 'bg-indigo-50 text-indigo-700'
                          : 'text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="flex-1 text-left">{item.label}</span>
                      <ChevronRight className={`w-4 h-4 transition-transform ${isActive ? 'rotate-90' : ''}`} />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* 内容区域 */}
          <div className="lg:col-span-9">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-2xl shadow-sm border border-slate-200 min-h-[600px]"
            >
              {/* 页面标题 */}
              <div className="p-6 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  {activeMenuItem && (
                    <>
                      <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center">
                        <activeMenuItem.icon className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div>
                        <h1 className="text-xl font-bold text-slate-800">{activeMenuItem.label}</h1>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* 页面内容 */}
              <div className="p-6">
                <ActiveComponent />
              </div>
            </motion.div>
          </div>
        </div>
      </main>
    </div>
  );
}

// 包装组件，添加路由保护
export default function Profile() {
  return (
    <PrivateRoute>
      <ProfileContent />
    </PrivateRoute>
  );
}
